import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  computed,
  inject,
  Injectable,
  PLATFORM_ID,
  signal
} from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface AdminUser {
  sub?: number;
  id?: number;
  email: string;
  name?: string;
  display_name?: string;
  role: 'admin' | 'editor';
}

interface LoginResponse {
  token: string;
  user: AdminUser;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly tokenKey = 'agbe-admin-token';
  private readonly userKey = 'agbe-admin-user';

  readonly user = signal<AdminUser | null>(null);
  readonly isAuthenticated = computed(() => this.user() !== null);

  constructor() {
    this.restoreSession();
  }

  get token(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    return localStorage.getItem(this.tokenKey);
  }

  validate(): Observable<boolean> {
    return of(this.token !== null);
  }

  login(email: string, password: string) {
    return this.http
      .post<LoginResponse>(
        `${environment.apiUrl}/auth/login`,
        { email, password }
      )
      .pipe(
        tap(({ token, user }) => {
          if (!isPlatformBrowser(this.platformId)) {
            return;
          }

          localStorage.setItem(this.tokenKey, token);
          localStorage.setItem(this.userKey, JSON.stringify(user));

          this.user.set(user);
        })
      );
  }

  logout(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(this.tokenKey);
      localStorage.removeItem(this.userKey);
    }

    this.user.set(null);
    this.router.navigateByUrl('/');
  }

  private restoreSession(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const token = localStorage.getItem(this.tokenKey);
    const storedUser = localStorage.getItem(this.userKey);

    if (!token || !storedUser) {
      this.clearSession();
      return;
    }

    try {
      const user = JSON.parse(storedUser) as AdminUser;
      this.user.set(user);
    } catch {
      this.clearSession();
    }
  }

  private clearSession(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(this.tokenKey);
      localStorage.removeItem(this.userKey);
    }

    this.user.set(null);
  }
}